import { useSettingsHighlight } from "#app/components/settings-search/settings-highlight";
import React, { Fragment, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  DND_DRAGGABLE_PROPS,
  DND_OVERLAY_PROPS,
  DND_TOUCH_ACTIVATION,
  Icon,
  type IconName,
  IconTile,
  Separator,
  sortableStyle,
  SPRING_DROP_ANIMATION,
  Text,
} from "@salestrak/ui";
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Sortable context for SettingsGroup descendants
// ---------------------------------------------------------------------------
//
// SettingsGroup owns the DnD wiring. SettingsGroupRow opts in per row with
// `sortableId` and a hover-revealed grip is overlaid in the row's existing
// left-padding gutter — no other visual change to the row.

type SettingsGroupSortableConfig = Omittable<{
  items: string[];
  onReorder: (activeId: string, overId: string) => void | Promise<unknown>;
  renderOverlay?: (id: string) => React.ReactNode;
}>;

interface SortableGroupContextValue {
  enabled: boolean;
  activeId: string | null;
}

const SortableGroupContext = React.createContext<SortableGroupContextValue>({
  enabled: false,
  activeId: null,
});

// ---------------------------------------------------------------------------
// SettingsGroup — outer wrapper with optional label, subtitle, and auto-separated Card
// ---------------------------------------------------------------------------

type SettingsGroupProps = Omittable<{
  label?: string;
  subtitle?: string;
  /** Visible flow items only (rows, headers, content blocks). Every child
   *  after the first earns a row separator, so a child that renders nothing
   *  in flow — a closed dialog, a portal — paints a phantom divider under
   *  the last real row. Mount overlays as SIBLINGS of the group. */
  children: React.ReactNode;
  sortable?: SettingsGroupSortableConfig;
}>;

function sameOrder(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

function sortableIdOf(child: React.ReactNode): string | undefined {
  if (!React.isValidElement(child)) return undefined;
  const id = (child.props as { sortableId?: unknown }).sortableId;
  return typeof id === "string" ? id : undefined;
}

// Keyed by sortable id so a row keeps its DOM node when the order changes,
// which is the node dnd-kit measures for the landing.
function childKey(child: React.ReactNode, index: number): React.Key {
  const id = sortableIdOf(child);
  return id === undefined ? index : `sortable:${id}`;
}

// The sortable rows trade places among their own slots; every other child
// stays where the caller put it.
function orderSortableChildren(
  children: React.ReactNode[],
  order: string[] | null,
): React.ReactNode[] {
  if (!order) return children;
  const byId = new Map<string, React.ReactNode>();
  for (const child of children) {
    const id = sortableIdOf(child);
    if (id !== undefined) byId.set(id, child);
  }
  const queue = order.filter((id) => byId.has(id)).map((id) => byId.get(id));
  return children.map((child) => (sortableIdOf(child) === undefined ? child : queue.shift()));
}

export function SettingsGroup({
  label,
  subtitle,
  children,
  sortable,
}: SettingsGroupProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  // The order a drop asked for, held until the caller's `items` move. dnd-kit
  // measures the landing slot in the same render that ends the drag, so the
  // rows must already stand in the new order there, or the overlay flies back
  // to the old slot and the list jumps when the write lands.
  const [dropped, setDropped] = useState<{ order: string[]; basis: string[] } | null>(
    null,
  );
  const pendingOrder =
    sortable && dropped && sameOrder(dropped.basis, sortable.items) ? dropped.order : null;
  const sortableItems = pendingOrder ?? sortable?.items ?? [];
  const items = orderSortableChildren(React.Children.toArray(children), pendingOrder);

  // Capability-based, never viewport-gated: a mouse drags after 8px of
  // travel, a finger after a hold (a swipe before the hold ends scrolls).
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: { distance: 8 },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: DND_TOUCH_ACTIVATION,
  });
  const sensors = useSensors(
    ...(sortable ? [mouseSensor, touchSensor] : []),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id || !sortable) return;
    const from = sortableItems.indexOf(String(active.id));
    const to = sortableItems.indexOf(String(over.id));
    if (from >= 0 && to >= 0) {
      const dropOrder = { order: arrayMove(sortableItems, from, to), basis: sortable.items };
      setDropped(dropOrder);
      void Promise.resolve(sortable.onReorder(String(active.id), String(over.id))).finally(
        () => setDropped((current) => (current === dropOrder ? null : current)),
      );
      return;
    }
    sortable.onReorder(String(active.id), String(over.id));
  };

  const handleDragCancel = () => setActiveId(null);

  const contextValue = useMemo<SortableGroupContextValue>(
    () => ({ enabled: !!sortable, activeId }),
    [sortable, activeId],
  );

  const card = (
    <Card>
      {items.map((child, index) => {
        if (index === 0) return <Fragment key={childKey(child, index)}>{child}</Fragment>;
        const prev = items[index - 1];
        const afterHeader =
          React.isValidElement(prev) &&
          (((prev.type as { __settingsGroupHeader?: boolean })
            .__settingsGroupHeader ??
            false) ||
            (prev.props as { header?: boolean }).header === true);
        return (
          <Fragment key={childKey(child, index)}>
            {afterHeader ? (
              <Separator />
            ) : (
              <Box paddingX="md">
                <Separator />
              </Box>
            )}
            {child}
          </Fragment>
        );
      })}
    </Card>
  );

  const header = label ? (
    <SettingsSectionHeader label={label} subtitle={subtitle} />
  ) : null;

  if (!sortable) {
    return (
      <Box direction="column" gap="md">
        {header}
        {card}
      </Box>
    );
  }

  return (
    <SortableGroupContext.Provider value={contextValue}>
      <Box direction="column" gap="md">
        {header}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext
            items={sortableItems}
            strategy={verticalListSortingStrategy}
          >
            {card}
          </SortableContext>
          {sortable.renderOverlay ? (
            <DragOverlay dropAnimation={SPRING_DROP_ANIMATION} {...DND_OVERLAY_PROPS}>
              {activeId ? <Card>{sortable.renderOverlay(activeId)}</Card> : null}
            </DragOverlay>
          ) : null}
        </DndContext>
      </Box>
    </SortableGroupContext.Provider>
  );
}

// The section header sits one type rung ABOVE the row titles (16px over
// 14px) so groups scan before rows do — a group label at the row rung reads
// as just another row. Its 16px inset puts it on the genre's text rail:
// rows pad 16px inside the card, so the label aligns with row text and the
// card bleeds past it on both sides.
function SettingsSectionHeader({
  label,
  subtitle,
}: Omittable<{ label: string; subtitle?: string }>) {
  return (
    <Box direction="column" gap="xs" paddingX="md">
      <Text weight="medium" size="base">
        {label}
      </Text>
      {subtitle ? (
        <Text size="sm" variant="muted">
          {subtitle}
        </Text>
      ) : null}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// SettingsSection — a labeled run of MULTIPLE cards. The header is the same
// treatment a labeled SettingsGroup renders; the children are LABEL-LESS
// SettingsGroups stacking below it on the section's 16px rhythm, so related
// cards can share one heading (e.g. interface rows + a separate theme card).
// ---------------------------------------------------------------------------

type SettingsSectionProps = Omittable<{
  label: string;
  subtitle?: string;
  children: React.ReactNode;
}>;

export function SettingsSection({
  label,
  subtitle,
  children,
}: SettingsSectionProps) {
  return (
    <Box direction="column" gap="md">
      <SettingsSectionHeader label={label} subtitle={subtitle} />
      {/* Header→first card keeps the genre's 16px; the cards THEMSELVES sit
          tighter (12px) so a multi-card section reads as one block. */}
      <Box direction="column" gap="base">{children}</Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// RowLabel — shared label+subtitle+leading layout used by both row modes
// ---------------------------------------------------------------------------

function RowLabel({
  label,
  labelAccessory,
  subtitle,
  leading,
  icon,
  quiet,
}: Omittable<{
  label: React.ReactNode;
  labelAccessory?: React.ReactNode;
  subtitle?: React.ReactNode;
  leading?: React.ReactNode;
  icon?: IconName;
  /** Mute the label: it names a part of the heading above rather than a
   *  setting of its own. See the label comment below. */
  quiet?: boolean;
}>) {
  // String labels get the standard title treatment; node labels render as-is
  // so rows can pass a self-styled block (e.g. an EntityTag with its own
  // visual + description) without an illegal Text wrapper around it.
  //
  // A setting's label takes the BODY ink: it names the thing being changed and
  // is the line a reader scans for. Muting every one made a whole page read as
  // secondary to nothing in particular. Titles still separate by weight and by
  // the full-bleed rule under them.
  //
  // `quiet` is the opt-out, for a label subordinate to a heading it sits UNDER
  // rather than beside — the colour rows of a theme section, where "Accent"
  // names a part of the theme named on the row above it.
  const labelNode =
    typeof label === "string" ? (
      <Text
        leading="tight"
        size="sm"
        variant={quiet ? "muted" : undefined}
        weight="medium"
      >
        {label}
      </Text>
    ) : (
      label
    );
  // `icon` is the standardized leading treatment: the library IconTile — a
  // 32px surface-relative well with the glyph in muted ink. `leading` remains
  // the freeform slot (entity avatars, status swatches); `icon` wins when
  // both are passed.
  const leadingNode = icon ? (
    <IconTile name={icon} size="lg" color="muted" />
  ) : (
    leading
  );
  return (
    <Box direction="row" gap="base" align="center" shrink minWidth={0}>
      {leadingNode}
      <Box direction="column" gap="xs" shrink minWidth={0}>
        {labelAccessory ? (
          <Box direction="row" gap="sm" align="center">
            {labelNode}
            {labelAccessory}
          </Box>
        ) : (
          labelNode
        )}
        {typeof subtitle === "string" ? (
          <Text size="xs" variant="muted" leading="tight">
            {subtitle}
          </Text>
        ) : (
          subtitle ?? null
        )}
      </Box>
    </Box>
  );
}

// The state-led meta line: a state word in its own ink ("Enabled" in success
// ink, "Disabled" muted), then " · " and a detail that ellipsizes rather than
// pushing the row's trailing controls out — a webhook URL, a key's scope.
// The state leads so a scan down the list reads the states in one column.
export function SettingsGroupRowMeta({
  state,
  tone = "muted",
  detail,
}: Omittable<{
  state: string;
  tone?: "success" | "muted";
  detail?: string;
}>) {
  return (
    <Box direction="row" align="center" gap="xs" minWidth={0}>
      <Text size="xs" variant={tone} leading="tight" weight="medium">
        {state}
      </Text>
      {detail ? (
        <>
          <Text size="xs" variant="muted" leading="tight">
            ·
          </Text>
          <Text size="xs" variant="muted" leading="tight" truncate overflowTooltip>
            {detail}
          </Text>
        </>
      ) : null}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// SettingsGroupRow — label+subtitle left, children right
//
// Three modes:
//   Static (no onClick):     children always visible, no hover effects —
//                            unless `hoverActions` opts into the pressable
//                            rows' hover-reveal (for list rows that lost
//                            their press but keep a "…" menu)
//   Pressable (onClick set): hover background, cursor pointer, hover-reveal children
//   Sortable (sortableId set): wraps in useSortable, overlays grip-vertical in
//                              the existing left-padding gutter on hover
// ---------------------------------------------------------------------------

type SettingsGroupRowProps = Omittable<{
  /**
   * This row is the card's TITLE, not one of its settings.
   *
   * The rule beneath it runs the FULL WIDTH of the card, while rules between
   * sibling settings stay inset — the reference's grammar, and the thing that
   * makes a title read as a title rather than as the first item of a list. The
   * alternative, `SettingsGroup`'s own `label`, floats ABOVE the card, which is
   * a different shape this pane does not use.
   */
  header?: boolean;
  /** Mute the label — it names a part of the heading above, not a setting of
   *  its own. The colour rows of a theme section are the case. */
  quiet?: boolean;
  label: React.ReactNode;
  labelAccessory?: React.ReactNode;
  /** The meta line under the label. A string takes the standard 12px muted
   *  treatment; a node renders as-is so a row can colour one word of it (a
   *  state word) or truncate one part (a URL) — see `SettingsGroupRowMeta`. */
  subtitle?: React.ReactNode;
  leading?: React.ReactNode;
  /** Standardized leading visual: the glyph in a 32px muted well. Wins over
   *  `leading`. */
  icon?: IconName;
  /** Quiet trailing state ("Active", "3 stages", "On") in the muted meta ink.
   *  Pressable rows pair it with the automatic chevron — never hand-roll a
   *  `Text` + `chevron-right` cluster in `children`. */
  value?: string;
  onClick?: () => void;
  /** `false` drops the pressable row's automatic chevron, for a list that
   *  carries its navigation affordance some other way — a trailing "…" menu
   *  in the same slot, plus the row's own hover wash. Per list, not a global
   *  rule: other drill-in rows keep the chevron beside their menu. */
  chevron?: boolean;
  actionsVisible?: boolean;
  // Static rows only: reveal children on hover, matching pressable rows.
  hoverActions?: boolean;
  sortableId?: string;
  /** Registers this row as the settings-sidebar search's jump target: a
   *  requested highlight naming this anchor scrolls the row to center and
   *  flashes the selected wash. Anchors come from the page's shared
   *  `*_ANCHORS` object in `components/settings-search/rows`. */
  highlightAnchor?: string;
  children?: React.ReactNode;
}>;

export function SettingsGroupRow(props: SettingsGroupRowProps) {
  const { sortableId } = props;
  if (sortableId) {
    return <SortableRow {...props} sortableId={sortableId} />;
  }
  return <StaticRow {...props} />;
}

// The quiet trailing cluster: the meta value in 12px muted ink, and on
// pressable rows the faint chevron that marks the row as navigation. The
// value hugs the chevron (4px) so the pair reads as one affordance.
function RowTrail({ value, pressable }: Omittable<{ value?: string; pressable?: boolean }>) {
  if (value == null && !pressable) return null;
  return (
    <Box direction="row" align="center" gap="xs" flex="none">
      {value != null ? (
        <Text size="xs" variant="muted">
          {value}
        </Text>
      ) : null}
      {pressable ? <Icon name="chevron-right" size="sm" color="faint" /> : null}
    </Box>
  );
}

function StaticRow({
  quiet,
  label,
  labelAccessory,
  subtitle,
  leading,
  icon,
  value,
  onClick,
  chevron,
  actionsVisible,
  hoverActions,
  highlightAnchor,
  children,
}: SettingsGroupRowProps) {
  const highlight = useSettingsHighlight(highlightAnchor);
  // Only an anchored row carries the wash transition: on every row it would
  // slow the hover wash itself.
  const highlightProps = highlightAnchor
    ? ({
        ref: highlight.ref,
        background: highlight.lit ? ("selected" as const) : undefined,
        className: "transition-colors duration-500",
      } as const)
    : {};
  // hoverActions rows share the pressable rows' exact geometry so a list can
  // mix both without the rows reading differently — only the press
  // affordances (hover wash, pointer, onClick) are gated.
  if (onClick || hoverActions) {
    return (
      // Not a real button element: rows host their own buttons and menu
      // triggers, and a button may not nest inside another button.
      <Box
        {...highlightProps}
        group="settings-row"
        direction="row"
        gap="md"
        align="center"
        justify="between"
        fill="width"
        paddingX="md"
        paddingY="base"
        minHeight={60}
        hoverBackground={onClick ? true : undefined}
        cursor={onClick ? "pointer" : undefined}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        onClick={onClick ? () => onClick() : undefined}
      >
        <RowLabel
          quiet={quiet}
          label={label}
          labelAccessory={labelAccessory}
          subtitle={subtitle}
          leading={leading}
          icon={icon}
        />

        <Box direction="row" align="center" gap="sm" flex="none">
          {children ? (
            <Box
              hoverReveal={actionsVisible ? undefined : "settings-row"}
              direction="row"
              align="center"
              flex="none"
              onClick={(e) => e.stopPropagation()}
            >
              {children}
            </Box>
          ) : null}
          <RowTrail value={value} pressable={!!onClick && chevron !== false} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      {...highlightProps}
      direction="column"
      paddingX="md"
      paddingY="base"
      justify="center"
      minHeight={60}
    >
      <Box direction="row" gap="lg" align="center" justify="between">
        <RowLabel
          quiet={quiet}
          label={label}
          labelAccessory={labelAccessory}
          subtitle={subtitle}
          leading={leading}
          icon={icon}
        />
        {children || value != null ? (
          <Box direction="row" align="center" gap="sm" flex="none">
            {children ? (
              <Box direction="row" align="end" flex="none">
                {children}
              </Box>
            ) : null}
            <RowTrail value={value} />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

function SortableRow({
  label,
  subtitle,
  leading,
  icon,
  value,
  sortableId,
  children,
}: SettingsGroupRowProps & { sortableId: string }) {
  const { enabled } = React.useContext(SortableGroupContext);
  const {
    attributes: { role: _role, ...attributes },
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({
    id: sortableId,
    disabled: !enabled,
  });

  const draggable = enabled;

  return (
    <Box
      group="settings-row"
      ref={setNodeRef}
      style={sortableStyle(transform, { transition, isSorting, isDragging, dragOpacity: 0.3 })}
      cursor={!draggable ? undefined : isDragging ? "grabbing" : "grab"}
      position="relative"
      // The whole row is the handle AND the page's scrollable content, so it
      // keeps panning (the draggable marker's CSS would set `touch-action:
      // none`); the hold-activated touch sensor prevents the scroll once a
      // hold completes.
      touchAction={draggable ? "manipulation" : undefined}
      {...(draggable ? (attributes as any) : undefined)}
      {...(draggable ? (listeners as any) : undefined)}
      {...(draggable ? DND_DRAGGABLE_PROPS : undefined)}
    >
      {draggable ? (
        <Box
          hoverReveal="settings-row"
          className="pointer-events-none"
          position="absolute"
          left={0}
          top={0}
          bottom={0}
          width={16}
          justify="center"
          align="center"
        >
          <Icon name="grip-vertical" size="sm" color="muted" />
        </Box>
      ) : null}
      <Box direction="column" paddingX="md" paddingY="base" justify="center" minHeight={60}>
        <Box direction="row" gap="lg" align="center" justify="between">
          <RowLabel label={label} subtitle={subtitle} leading={leading} icon={icon} />
          {children || value != null ? (
            <Box
              direction="row"
              align="center"
              gap="sm"
              flex="none"
              onPointerDown={(e) => e.stopPropagation()}
            >
              {children}
              <RowTrail value={value} />
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// SettingsGroupHeader — count + action row (e.g. "5 webhooks" + plus button)
// ---------------------------------------------------------------------------

interface SettingsGroupHeaderProps {
  children: React.ReactNode;
}

export function SettingsGroupHeader({ children }: SettingsGroupHeaderProps) {
  return (
    <Box direction="row" align="center" justify="between" paddingX="md" paddingY="md">
      {children}
    </Box>
  );
}
(
  SettingsGroupHeader as typeof SettingsGroupHeader & {
    __settingsGroupHeader?: boolean;
  }
).__settingsGroupHeader = true;

// ---------------------------------------------------------------------------
// SettingsGroupEmptyRow — the empty-list row: quiet noun-state text on the
// left ("No webhooks"), a named create action on the right ("+ New webhook").
// The named action is the section's create affordance while the list is
// empty, so a section that renders this row hides its header plus button.
// ---------------------------------------------------------------------------

type SettingsGroupEmptyRowProps = Omittable<{
  label: string;
  actionLabel?: string;
  onAction?: () => void;
}>;

export function SettingsGroupEmptyRow({
  label,
  actionLabel,
  onAction,
}: SettingsGroupEmptyRowProps) {
  return (
    <Box
      direction="row"
      gap="md"
      align="center"
      justify="between"
      paddingX="md"
      paddingY="base"
      minHeight={60}
    >
      <Text size="sm" variant="muted">
        {label}
      </Text>
      {actionLabel && onAction ? (
        <Button variant="ghost" onClick={onAction}>
          {actionLabel}
        </Button>
      ) : null}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// SettingsGroupContent — arbitrary padded content (forms, custom layouts)
// ---------------------------------------------------------------------------

interface SettingsGroupContentProps {
  children: React.ReactNode;
}

export function SettingsGroupContent({ children }: SettingsGroupContentProps) {
  return <Box padding="md">{children}</Box>;
}
